import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

const slotsInput = z.object({
  agentId: z.string().uuid().nullish(),
  email: z.string().email().nullish(),
  days: z.number().int().min(1).max(60).optional(),
});

const bookInput = z.object({
  agentId: z.string().uuid().nullish(),
  slotStart: z.string().min(10),
  name: z.string().trim().min(1).max(120),
  email: z.string().trim().email().max(200),
  phone: z.string().trim().max(40).nullish(),
  notes: z.string().trim().max(2000).nullish(),
  timezone: z.string().max(64).nullish(),
});

const windowsInput = z.object({
  windows: z.array(
    z.object({
      weekday: z.number().int().min(0).max(6),
      start_minute: z.number().int().min(0).max(1439),
      end_minute: z.number().int().min(1).max(1440),
      slot_minutes: z.number().int().min(15).max(120),
      timezone: z.string().min(1).max(64),
    }),
  ).max(40),
});

export const getOpenSlots = createServerFn({ method: "GET" })
  .inputValidator((d: unknown) => slotsInput.parse(d ?? {}))
  .handler(async ({ data }) => {
    const { listOpenSlots } = await import("@/lib/booking.server");
    return listOpenSlots({ agentId: data.agentId, email: data.email, days: data.days });
  });

/** Public booking — /book is open to families without an account. */
export const bookSlot = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => bookInput.parse(d))
  .handler(async ({ data }) => {
    const { createBooking } = await import("@/lib/booking.server");
    return createBooking(data);
  });

export const bookSlotAsClient = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => bookInput.parse(d))
  .handler(async ({ data, context }) => {
    const { createBooking } = await import("@/lib/booking.server");
    return createBooking({ ...data, userId: context.userId });
  });

export const cancelAppointment = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ appointmentId: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    const { cancelBooking } = await import("@/lib/booking.server");
    return cancelBooking(data.appointmentId, context.userId);
  });

export const rescheduleAppointment = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ appointmentId: z.string().uuid(), slotStart: z.string().min(10) }).parse(d))
  .handler(async ({ data, context }) => {
    const { rescheduleBooking } = await import("@/lib/booking.server");
    return rescheduleBooking(data.appointmentId, data.slotStart, context.userId);
  });

export const getMyAvailability = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { listAgentAvailability } = await import("@/lib/booking.server");
    return listAgentAvailability(context.userId);
  });

export const saveMyAvailability = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => windowsInput.parse(d))
  .handler(async ({ data, context }) => {
    const { saveAgentAvailability } = await import("@/lib/booking.server");
    return saveAgentAvailability(context.userId, data.windows);
  });

export const getMyAppointments = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase } = context;
    const { data, error } = await supabase
      .from("appointments")
      .select("id, scheduled_at, ends_at, duration_minutes, status, notes, agent_id")
      .order("scheduled_at", { ascending: true });
    if (error) throw error;
    return data ?? [];
  });
